import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Progress,
    Tag,
    Alert,
    List,
    Tabs,
    Select,
    DatePicker,
    Button,
    Space
} from 'antd';
import {
    TrophyOutlined,
    RiseOutlined,
    BankOutlined,
    BarChartOutlined,
    LineChartOutlined,
    PieChartOutlined,
    WarningOutlined,
    BulbOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { savingsReportService } from '../../services/savingsReportService';
import { savingsService } from '../../services/savingsService';
import { useAuth } from '../../../auth/contexts/AuthContext';
import { formatCurrency } from '../../../../shared/utils/formatters';
import dayjs from 'dayjs';
import styles from './SavingsReport.module.css';

const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;

export default function SavingsReport() {
    const { currentUser } = useAuth();
    const [savings, setSavings] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reportPeriod, setReportPeriod] = useState('all');
    const [dateRange, setDateRange] = useState([]);

    useEffect(() => {
        if (currentUser) {
            fetchData();
        }
    }, [currentUser]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [savingsData, transactionsData] = await Promise.all([
                savingsService.getSavings(currentUser.uid),
                savingsService.getSavingsTransactions(currentUser.uid)
            ]);
            setSavings(savingsData);
            setTransactions(transactionsData);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        setLoading(false);
    };

    // Filter data based on period
    const filteredData = useMemo(() => {
        let filteredSavings = savings;
        let filteredTransactions = transactions;

        if (reportPeriod !== 'all') {
            const now = dayjs();
            let startDate;

            switch (reportPeriod) {
                case '1month':
                    startDate = now.subtract(1, 'month');
                    break;
                case '3months':
                    startDate = now.subtract(3, 'month');
                    break;
                case '6months':
                    startDate = now.subtract(6, 'month');
                    break;
                case '1year':
                    startDate = now.subtract(1, 'year');
                    break;
                case 'custom':
                    if (dateRange.length === 2) {
                        startDate = dateRange[0];
                        const endDate = dateRange[1];
                        filteredSavings = savings.filter(s =>
                            dayjs(s.ngayTao).isBetween(startDate, endDate, null, '[]')
                        );
                        filteredTransactions = transactions.filter(t =>
                            dayjs(t.ngayGiaoDich).isBetween(startDate, endDate, null, '[]')
                        );
                    }
                    break;
                default:
                    startDate = now.subtract(1, 'year');
            }

            if (reportPeriod !== 'custom') {
                filteredSavings = savings.filter(s =>
                    dayjs(s.ngayTao).isAfter(startDate)
                );
                filteredTransactions = transactions.filter(t =>
                    dayjs(t.ngayGiaoDich).isAfter(startDate)
                );
            }
        }

        return { filteredSavings, filteredTransactions };
    }, [savings, transactions, reportPeriod, dateRange]);

    // Generate reports
    const overviewReport = useMemo(() =>
        savingsReportService.generateOverviewReport(filteredData.filteredSavings, filteredData.filteredTransactions),
        [filteredData]
    );

    const bankReport = useMemo(() =>
        savingsReportService.generateBankReport(filteredData.filteredSavings),
        [filteredData.filteredSavings]
    );

    const termReport = useMemo(() =>
        savingsReportService.generateTermReport(filteredData.filteredSavings),
        [filteredData.filteredSavings]
    );

    const performanceReport = useMemo(() =>
        savingsReportService.generatePerformanceReport(filteredData.filteredSavings, filteredData.filteredTransactions),
        [filteredData]
    );

    const growthForecast = useMemo(() =>
        savingsReportService.generateGrowthForecast(filteredData.filteredSavings),
        [filteredData.filteredSavings]
    );

    const riskAnalysis = useMemo(() =>
        savingsReportService.generateRiskAnalysis(filteredData.filteredSavings),
        [filteredData.filteredSavings]
    );

    // Chart data
    const performanceChartData = {
        labels: performanceReport.map(p => p.month),
        datasets: [
            {
                label: 'Tiết kiệm mới',
                data: performanceReport.map(p => p.newPrincipal),
                borderColor: '#52c41a',
                backgroundColor: 'rgba(82, 196, 26, 0.1)',
                tension: 0.4
            },
            {
                label: 'Tổng giá trị',
                data: performanceReport.map(p => p.totalValue),
                borderColor: '#1890ff',
                backgroundColor: 'rgba(24, 144, 255, 0.1)',
                tension: 0.4,
                yAxisID: 'y1'
            }
        ]
    };

    const bankDistributionData = {
        labels: bankReport.map(b => b.bank),
        datasets: [{
            data: bankReport.map(b => b.totalCurrentValue),
            backgroundColor: [
                '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
                '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF'
            ],
            borderWidth: 2
        }]
    };

    const forecastChartData = {
        labels: growthForecast.map(f => f.month),
        datasets: [{
            label: 'Dự báo giá trị',
            data: growthForecast.map(f => f.forecastValue),
            borderColor: '#722ed1',
            backgroundColor: 'rgba(114, 46, 209, 0.1)',
            borderDash: [5, 5],
            tension: 0.4
        }]
    };

    const getRiskColor = (level) => {
        switch (level) {
            case 'low': return 'success';
            case 'medium': return 'warning';
            case 'high': return 'error';
            default: return 'default';
        }
    };

    const bankColumns = [
        {
            title: 'Ngân hàng',
            dataIndex: 'bank',
            key: 'bank',
            render: (bank) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <BankOutlined />
                    <strong>{bank}</strong>
                </div>
            )
        },
        {
            title: 'Số khoản',
            dataIndex: 'count',
            key: 'count',
            render: (count, record) => (
                <div>
                    <div>{count} khoản</div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                        {record.activeSavings} đang hoạt động
                    </div>
                </div>
            )
        },
        {
            title: 'Tổng tiền gốc',
            dataIndex: 'totalPrincipal',
            key: 'totalPrincipal',
            render: (amount) => formatCurrency(amount),
            sorter: (a, b) => a.totalPrincipal - b.totalPrincipal
        },
        {
            title: 'Giá trị hiện tại',
            dataIndex: 'totalCurrentValue',
            key: 'totalCurrentValue',
            render: (amount) => (
                <strong style={{ color: '#52c41a' }}>
                    {formatCurrency(amount)}
                </strong>
            ),
            sorter: (a, b) => a.totalCurrentValue - b.totalCurrentValue
        },
        {
            title: 'Lãi tích lũy',
            dataIndex: 'totalInterest',
            key: 'totalInterest',
            render: (interest) => (
                <span style={{ color: '#fa8c16' }}>
                    {formatCurrency(interest)}
                </span>
            )
        },
        {
            title: 'Lãi suất TB',
            dataIndex: 'averageRate',
            key: 'averageRate',
            render: (rate) => `${rate.toFixed(2)}%`,
            sorter: (a, b) => a.averageRate - b.averageRate
        }
    ];

    const termColumns = [
        {
            title: 'Kỳ hạn',
            dataIndex: 'term',
            key: 'term'
        },
        {
            title: 'Số khoản',
            dataIndex: 'count',
            key: 'count'
        },
        {
            title: 'Tổng tiền gốc',
            dataIndex: 'totalPrincipal',
            key: 'totalPrincipal',
            render: (amount) => formatCurrency(amount)
        },
        {
            title: 'Giá trị hiện tại',
            dataIndex: 'totalCurrentValue',
            key: 'totalCurrentValue',
            render: (amount) => formatCurrency(amount)
        },
        {
            title: 'Lãi suất TB',
            dataIndex: 'averageRate',
            key: 'averageRate',
            render: (rate) => `${rate.toFixed(2)}%`
        }
    ];

    return (
        <div className={styles.savingsReport}>
            {/* Header với filter */}
            <Card className={styles.filterCard}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <h2>📊 Báo cáo thống kê tiết kiệm</h2>
                    </Col>
                    <Col>
                        <Space>
                            <Select
                                value={reportPeriod}
                                onChange={setReportPeriod}
                                style={{ width: 150 }}
                            >
                                <Option value="all">Tất cả</Option>
                                <Option value="1month">1 tháng</Option>
                                <Option value="3months">3 tháng</Option>
                                <Option value="6months">6 tháng</Option>
                                <Option value="1year">1 năm</Option>
                                <Option value="custom">Tùy chọn</Option>
                            </Select>

                            {reportPeriod === 'custom' && (
                                <RangePicker
                                    value={dateRange}
                                    onChange={setDateRange}
                                    format="DD/MM/YYYY"
                                />
                            )}

                            <Button icon={<DownloadOutlined />}>
                                Xuất báo cáo
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Tabs defaultActiveKey="overview" className={styles.reportTabs}>
                {/* Tổng quan */}
                <TabPane tab="📈 Tổng quan" key="overview">
                    <Row gutter={[16, 16]}>
                        {/* Thống kê chính */}
                        <Col span={24}>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Card>
                                        <Statistic
                                            title="Tổng số khoản"
                                            value={overviewReport.totalSavings}
                                            suffix="khoản"
                                            prefix={<BankOutlined />}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                            {overviewReport.activeSavings} đang hoạt động
                                        </div>
                                    </Card>
                                </Col>

                                <Col span={6}>
                                    <Card>
                                        <Statistic
                                            title="Tổng giá trị"
                                            value={overviewReport.totalCurrentValue}
                                            formatter={(value) => formatCurrency(value)}
                                            prefix={<TrophyOutlined />}
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                            Gốc: {formatCurrency(overviewReport.totalPrincipal)}
                                        </div>
                                    </Card>
                                </Col>

                                <Col span={6}>
                                    <Card>
                                        <Statistic
                                            title="Lãi tích lũy"
                                            value={overviewReport.totalInterestEarned}
                                            formatter={(value) => formatCurrency(value)}
                                            prefix={<RiseOutlined />}
                                            valueStyle={{ color: '#fa8c16' }}
                                        />
                                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                            Hiệu suất: {overviewReport.effectiveYield.toFixed(2)}%
                                        </div>
                                    </Card>
                                </Col>

                                <Col span={6}>
                                    <Card>
                                        <Statistic
                                            title="Lãi suất TB"
                                            value={overviewReport.averageInterestRate}
                                            suffix="%/năm"
                                            precision={2}
                                            valueStyle={{ color: '#722ed1' }}
                                        />
                                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                            Tăng trưởng: {overviewReport.growthRate.toFixed(1)}%
                                        </div>
                                    </Card>
                                </Col>
                            </Row>
                        </Col>

                        {/* Biểu đồ hiệu suất */}
                        <Col span={16}>
                            <Card title="📈 Hiệu suất theo thời gian">
                                <div style={{ height: 300 }}>
                                    <Line
                                        data={performanceChartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            scales: {
                                                y: {
                                                    type: 'linear',
                                                    display: true,
                                                    position: 'left',
                                                    beginAtZero: true,
                                                    ticks: {
                                                        callback: function (value) {
                                                            return formatCurrency(value);
                                                        }
                                                    }
                                                },
                                                y1: {
                                                    type: 'linear',
                                                    display: true,
                                                    position: 'right',
                                                    beginAtZero: true,
                                                    grid: {
                                                        drawOnChartArea: false,
                                                    },
                                                    ticks: {
                                                        callback: function (value) {
                                                            return formatCurrency(value);
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </Card>
                        </Col>

                        {/* Phân bổ theo ngân hàng */}
                        <Col span={8}>
                            <Card title="🏦 Phân bổ theo ngân hàng">
                                <div style={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <Doughnut
                                        data={bankDistributionData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {
                                                    position: 'bottom'
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </TabPane>

                {/* Phân tích theo ngân hàng */}
                <TabPane tab="🏦 Theo ngân hàng" key="bank">
                    <Card title="Thống kê theo ngân hàng">
                        <Table
                            columns={bankColumns}
                            dataSource={bankReport}
                            rowKey="bank"
                            pagination={false}
                            loading={loading}
                        />
                    </Card>
                </TabPane>

                {/* Phân tích theo kỳ hạn */}
                <TabPane tab="⏰ Theo kỳ hạn" key="term">
                    <Card title="Thống kê theo kỳ hạn">
                        <Table
                            columns={termColumns}
                            dataSource={termReport}
                            rowKey="term"
                            pagination={false}
                            loading={loading}
                        />
                    </Card>
                </TabPane>

                {/* Dự báo */}
                <TabPane tab="🔮 Dự báo" key="forecast">
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Card title="📊 Dự báo tăng trưởng 12 tháng">
                                <div style={{ height: 400 }}>
                                    <Line
                                        data={forecastChartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                    ticks: {
                                                        callback: function (value) {
                                                            return formatCurrency(value);
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </Card>
                        </Col>

                        <Col span={24}>
                            <Card title="📋 Chi tiết dự báo">
                                <Table
                                    dataSource={growthForecast}
                                    columns={[
                                        {
                                            title: 'Tháng',
                                            dataIndex: 'month',
                                            key: 'month'
                                        },
                                        {
                                            title: 'Giá trị dự báo',
                                            dataIndex: 'forecastValue',
                                            key: 'forecastValue',
                                            render: (value) => formatCurrency(value)
                                        },
                                        {
                                            title: 'Tăng trưởng',
                                            dataIndex: 'growth',
                                            key: 'growth',
                                            render: (growth) => (
                                                <span style={{ color: growth >= 0 ? '#52c41a' : '#ff4d4f' }}>
                                                    {growth >= 0 ? '+' : ''}{growth}%
                                                </span>
                                            )
                                        }
                                    ]}
                                    pagination={false}
                                    size="small"
                                    rowKey="month"
                                />
                            </Card>
                        </Col>
                    </Row>
                </TabPane>

                {/* Phân tích rủi ro */}
                <TabPane tab="⚠️ Rủi ro & Cơ hội" key="risk">
                    <Row gutter={[16, 16]}>
                        {/* Phân tích rủi ro */}
                        <Col span={12}>
                            <Card title="⚠️ Phân tích rủi ro">
                                <div style={{ marginBottom: 16 }}>
                                    <h4>Rủi ro tập trung</h4>
                                    <Progress
                                        percent={riskAnalysis.concentrationRisk.percentage}
                                        status={riskAnalysis.concentrationRisk.level === 'high' ? 'exception' : 'normal'}
                                        strokeColor={
                                            riskAnalysis.concentrationRisk.level === 'high' ? '#ff4d4f' :
                                                riskAnalysis.concentrationRisk.level === 'medium' ? '#faad14' : '#52c41a'
                                        }
                                    />
                                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                        {riskAnalysis.concentrationRisk.recommendation}
                                    </div>
                                </div>

                                <div style={{ marginBottom: 16 }}>
                                    <h4>Rủi ro thanh khoản</h4>
                                    <Progress
                                        percent={riskAnalysis.liquidityRisk.percentage}
                                        status={riskAnalysis.liquidityRisk.level === 'high' ? 'exception' : 'normal'}
                                        strokeColor={
                                            riskAnalysis.liquidityRisk.level === 'high' ? '#ff4d4f' :
                                                riskAnalysis.liquidityRisk.level === 'medium' ? '#faad14' : '#52c41a'
                                        }
                                    />
                                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                        {riskAnalysis.liquidityRisk.recommendation}
                                    </div>
                                </div>

                                <div>
                                    <h4>Rủi ro lãi suất</h4>
                                    <Tag color={getRiskColor(riskAnalysis.interestRateRisk.level)}>
                                        {riskAnalysis.interestRateRisk.level === 'high' ? 'Cao' :
                                            riskAnalysis.interestRateRisk.level === 'medium' ? 'Trung bình' : 'Thấp'}
                                    </Tag>
                                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                        Độ lệch chuẩn: {riskAnalysis.interestRateRisk.volatility.toFixed(2)}%
                                    </div>
                                    <div style={{ fontSize: 12, color: '#666' }}>
                                        {riskAnalysis.interestRateRisk.recommendation}
                                    </div>
                                </div>
                            </Card>
                        </Col>

                        {/* Cơ hội */}
                        <Col span={12}>
                            <Card title="💡 Cơ hội cải thiện">
                                {riskAnalysis.opportunities.length === 0 ? (
                                    <Alert
                                        message="Tuyệt vời!"
                                        description="Hiện tại không có cơ hội cải thiện nào được phát hiện."
                                        type="success"
                                        showIcon
                                    />
                                ) : (
                                    <List
                                        dataSource={riskAnalysis.opportunities}
                                        renderItem={opportunity => (
                                            <List.Item>
                                                <List.Item.Meta
                                                    avatar={<BulbOutlined style={{ color: '#faad14' }} />}
                                                    title={
                                                        <div>
                                                            {opportunity.title}
                                                            <Tag
                                                                color={opportunity.priority === 'high' ? 'red' : 'orange'}
                                                                style={{ marginLeft: 8 }}
                                                            >
                                                                {opportunity.priority === 'high' ? 'Ưu tiên cao' : 'Ưu tiên trung bình'}
                                                            </Tag>
                                                        </div>
                                                    }
                                                    description={opportunity.description}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                )}
                            </Card>
                        </Col>
                    </Row>
                </TabPane>
            </Tabs>
        </div>
    );
}
